import { FieldForm } from "@/features/question/components/organisms/FieldForm"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs"
import { crmSystem } from "@/constants/crm/_shared/systems"
import { crmRegistry } from "@/features/crm_system/lib/_core/crmRegistry"
import { AllCrmsFieldsTypes } from "@/features/crm_system/lib/_core/crmSystem"
import { matchConfigurationData, matchConfigurationDataWithDefault, matchDefaultConfigurationData } from "@/lib/utils/crm/matchConfigurationData"
import { zodResolver } from "@hookform/resolvers/zod"
import { Plus } from "lucide-react"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from "react"
import { useFieldArray, useForm } from "react-hook-form"
import { questionBankFormDefaultValues, QuestionBankFormProps, QuestionBankFormRef, questionBankFormSchema, QuestionBankFormSchemaType } from "."
import { merge } from "lodash"
import { DEFAULT_CRM_SYSTEM } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.constants.gen"
import { QuestionConfigurationParameterInput } from "../../../../parameters/components/atoms/QuestionConfigurationParameterInput"
import { ConditionGroup } from "@/features/question/components/molecules/ConditionGroup"
import { SelectCrmSystemSanitizedCombobox } from "@/features/crm_system/components/molecules/SelectCrmSystemSanitizedCombobox"
import { CrmSystemIds } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.types.gen"



/**
 * To use this component, you need to pass ref to it from the parent component:
 * <AddQuestionForm ref={formRef} ... />.
 * Use the ref to access the form methods. For example:
 * const formRef = useRef<AddQuestionFormRef>(null);
 * const form = addQuestionFormRef.current?.form;.
 * Then you can access methods like this: form?.handleSubmit
 */
export const QuestionBankForm = forwardRef<QuestionBankFormRef, QuestionBankFormProps>(({
    defaultValues = questionBankFormDefaultValues,
    crm,
    crmProject,
    crmConfig,
    isFormDisabled = false,
    questions,
}, ref) => {
    const form = useForm<QuestionBankFormSchemaType>({
        resolver: zodResolver(questionBankFormSchema)
    })
    const defaultValuesExtended = useMemo(() => (merge({}, questionBankFormDefaultValues, defaultValues, { crm_system: DEFAULT_CRM_SYSTEM })), [defaultValues])
    const watchConditionalsEnabled = form.watch("selected_configuration.conditionals.enabled");
    const { fields, append: appendConditionGroup, remove: removeConditionGroup } = useFieldArray({
        control: form.control,
        name: "selected_configuration.conditionals.where.or",
    });
    const matchedConfigurationWithDefault = useMemo(() => matchConfigurationDataWithDefault(crm, crmProject, crmConfig), [crm, crmProject, crmConfig]);
    const questionMatchedConfiguration = useMemo(
        () => matchConfigurationData(crm, crm?.configuration), [crm]
    )?.parameter?.question
    const questionDefaultMatchedConfiguration = useMemo(
        () => matchDefaultConfigurationData(crm), [crm]
    )?.parameter?.question
    // TODO PROVIDE FULL CONFIGURATION INSTEAD OF QUESTION CONFIGURATION
    const questionMatchedConfigurationWithDefault = useMemo(
        () => matchedConfigurationWithDefault?.parameter?.question, [matchedConfigurationWithDefault]
    );

    const resetForm = useCallback(() => {
        form.reset({
            ...defaultValuesExtended,
        })
    }, [defaultValuesExtended, form])

    useEffect(() => {
        resetForm()
    }, [defaultValuesExtended, resetForm])


    useImperativeHandle(ref, () => ({
        form,
        resetForm,
    }), [form, resetForm])
    const crmSystem = form.watch('crm_system') as CrmSystemIds;
    useEffect(() => {
        form.setValue('field_type', '');
    }, [crmSystem, form])

    const areQuestionsForConditionalsAvailable = (!questions || (questions?.length === 0));

    return (
        <Form {...form}>
            <form>
                <Tabs defaultValue="questionParameters">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="questionParameters">Question Parameters</TabsTrigger>
                        <TabsTrigger value="conditionals" disabled={areQuestionsForConditionalsAvailable}>
                            Conditionals
                            {watchConditionalsEnabled && (
                                <span className="flex items-center justify-center px-2 py-px text-xs font-medium text-white bg-blue-400 rounded-md ms-1">
                                    {fields.length}
                                </span>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="questionParameters">
                        <div className="grid grid-cols-2 gap-2 pb-1">
                            <div>
                                <FormField
                                    name="question"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Question</FormLabel>
                                            <FormControl>
                                                <Input {...field} autoComplete="off" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    name="description"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Description</FormLabel>
                                            <FormControl>
                                                <Input {...field} autoComplete="off" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />



                                {questionMatchedConfigurationWithDefault && questionMatchedConfiguration && (
                                    <>
                                        <h3 className="pt-2 my-3 font-semibold border-t">Configuration</h3>
                                        <QuestionConfigurationParameterInput
                                            crmSystem={crmSystem}
                                            parameters={[...(questionDefaultMatchedConfiguration || []), ...(questionMatchedConfiguration || [])]}
                                            form={form}
                                            fullConfiguration={matchedConfigurationWithDefault}
                                        />
                                    </>
                                )}


                            </div>
                            <div>
                                <FormField
                                    name="crm_system"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>CRM System</FormLabel>
                                            <FormControl>
                                                <SelectCrmSystemSanitizedCombobox
                                                    selectedValue={field.value}
                                                    setSelectedValue={field.onChange}
                                                    mode="single"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    name="field_type"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Field Type</FormLabel>
                                            <FormControl>
                                                <Select onValueChange={field.onChange} value={field.value} disabled={isFormDisabled}>
                                                    <SelectTrigger className="" >
                                                        <SelectValue placeholder={crmSystem ? "Select Question Type" : "Select CRM System first"} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {crmSystem && crmRegistry.getFieldsList(crmSystem).map((option) => (
                                                            <SelectItem key={option.id} value={option.id}>
                                                                {option.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                {crmSystem && (
                                    <FieldForm
                                        form={form}
                                        fieldType={form?.watch('field_type') as AllCrmsFieldsTypes}
                                        isFormDisabled={isFormDisabled}
                                        crmSystem={crmSystem}
                                    />
                                )}
                            </div>
                        </div>
                    </TabsContent>
                    <TabsContent value="conditionals">
                        {(questions) && (
                            <div className="flex flex-col gap-3 pt-2 mt-3 border-t">
                                <FormField
                                    name={"selected_configuration.conditionals.enabled"}
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem className="flex items-center gap-2 space-y-0">
                                            <Checkbox
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                            <FormLabel>Show question only when conditionals are met</FormLabel>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                {watchConditionalsEnabled && (
                                    <>
                                        <div className="flex flex-col gap-2">
                                            {fields.map((field, index, arr) => {
                                                return (
                                                    <ConditionGroup
                                                        key={field.id}
                                                        index={index}
                                                        form={form}
                                                        removeConditionGroup={removeConditionGroup}
                                                        questions={questions}
                                                        crmSystem={crmSystem as crmSystem || DEFAULT_CRM_SYSTEM}
                                                        length={arr.length}
                                                    />
                                                )
                                            })}
                                        </div>
                                        <div>
                                            <Button size="sm" variant="outline" type="button" className="mt-2" onClick={() => appendConditionGroup({})}>
                                                <Plus size="16" /> {fields.length > 0 ? 'OR' : 'Add condition group'}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        {areQuestionsForConditionalsAvailable && (
                            <p className='py-4 text-sm text-center'>Questions not found</p>
                        )}
                    </TabsContent>
                </Tabs>
            </form>
        </Form>
    )
})

